import { FC, useEffect } from 'react';
import { Box, Stack } from '@mui/material';
import {
  Button,
  Modal,
  RichTextInput,
  SelectInput,
  SortableList,
  TextInput,
  Typography,
} from 'melp-design/components';
import {
  Controller,
  SubmitHandler,
  useFieldArray,
  useForm,
} from 'react-hook-form-latest';
import { useTranslation } from 'react-i18next';
import { useSendTransmissionActPdf } from 'store/admin-transmission-acts';
import { ModalProps } from 'store/modal';
import {
  AppLanguage,
  ContentLanguage,
  appLanguages,
  uppercaseAppLanguages,
} from 'types/general';
import { formatLanguage } from 'utils/format';
import { Delete } from '@mui/icons-material';
import { toast } from 'react-toastify';
import {
  useAdminCompany,
  useAdminUpdateCompanySettings,
} from 'store/admin-companies';
import { useAdminUpdateClient } from 'store/admin-clients';
import { usePredefinedToasts } from 'utils/Toast';
import { escape } from 'lodash';
import { toQuillValue } from 'components/common/RichTextEditor/converters';

interface Values {
  accountantEmails: Array<{ email: string }>;
  accountantLanguage: Uppercase<AppLanguage>;
  contentText: string;
}

const INITIAL_VALUES: Values = {
  accountantEmails: [{ email: '' }],
  accountantLanguage: 'EN',
  contentText: '',
};

const CONTENT_TEXT_KEY = 'email.template.transmissionAct.contentText';

const toTranslationLanguage = (
  language: Uppercase<ContentLanguage>,
): Uppercase<AppLanguage> => {
  const lowercased = language.toLowerCase() as AppLanguage;
  return appLanguages.includes(lowercased)
    ? (language as Uppercase<AppLanguage>)
    : 'EN';
};

interface Props extends ModalProps {
  clientId: string;
  transmissionActId: string;
  transmissionActSequence: number;
  payload:
    | {
        companyId: string;
      }
    | {
        accountantEmails: Array<{ email: string }>;
        accountantLanguage: Uppercase<ContentLanguage>;
      };
}

export const SendTransmissionActModal: FC<Props> = ({
  clientId,
  transmissionActId,
  transmissionActSequence,
  payload,
  closeModal,
}) => {
  const { t, i18n } = useTranslation();
  const predefinedToasts = usePredefinedToasts();

  const { control, handleSubmit, getValues, reset, setValue, watch } = useForm({
    defaultValues: {
      ...('companyId' in payload
        ? INITIAL_VALUES
        : {
            accountantEmails: payload.accountantEmails,
            accountantLanguage: toTranslationLanguage(
              payload.accountantLanguage,
            ),
            contentText: '',
          }),
    },
  });
  const {
    fields: accountantEmailsFields,
    append: appendAccountantEmail,
    remove: removeAccountantEmail,
  } = useFieldArray({
    control,
    name: 'accountantEmails',
  });

  const { data: company } = useAdminCompany(
    'companyId' in payload ? payload.companyId : '',
    { enabled: 'companyId' in payload },
  );

  const { mutate: sendPdf, isLoading: isSendingPdf } =
    useSendTransmissionActPdf();
  const { mutate: updateCompany, isLoading: isUpdatingCompany } =
    useAdminUpdateCompanySettings();
  const { mutate: updateClient, isLoading: isUpdatingClient } =
    useAdminUpdateClient();

  useEffect(() => {
    if (company) {
      reset({
        accountantEmails: company.settings.accountantEmails.map((email) => ({
          email,
        })),
        accountantLanguage: toTranslationLanguage(
          company.settings.accountantLanguage,
        ),
        contentText: getValues('contentText'),
      });
    }
  }, [company, reset, getValues]);

  const [accountantLanguage] = watch(['accountantLanguage']);

  useEffect(() => {
    let isCancelled = false;
    const language = accountantLanguage.toLowerCase() as AppLanguage;

    i18n.loadLanguages([language, 'en']).then(() => {
      if (isCancelled) return;
      const contentText =
        i18n.getFixedT(language)(CONTENT_TEXT_KEY, { defaultValue: '' }) ||
        i18n.getFixedT('en')(CONTENT_TEXT_KEY, { defaultValue: '' });
      setValue('contentText', toQuillValue(escape(contentText)) ?? '');
    });

    return () => {
      isCancelled = true;
    };
  }, [accountantLanguage, i18n, setValue]);

  const onSubmit: SubmitHandler<Values> = (values) => {
    sendPdf(
      {
        transmissionActId,
        data: {
          recipients: values.accountantEmails.map(({ email }) => email),
          language: values.accountantLanguage as Uppercase<ContentLanguage>,
          contentText: values.contentText,
        },
      },
      {
        onSuccess: () => {
          toast.success(t('common.email_sent_successfully'));
          closeModal();
        },
      },
    );
  };

  const onUpdate = () => {
    const values = getValues();

    'companyId' in payload
      ? updateCompany(
          {
            companyId: payload.companyId,
            settings: {
              accountantEmails: values.accountantEmails.map(
                ({ email }) => email,
              ),
              accountantLanguage:
                values.accountantLanguage as Uppercase<ContentLanguage>,
            },
          },
          {
            onSuccess: () => {
              predefinedToasts.success.updated();
            },
          },
        )
      : updateClient(
          {
            clientId,
            data: {
              accountantEmails: values.accountantEmails.map(
                ({ email }) => email,
              ),
              accountantLanguage:
                values.accountantLanguage as Uppercase<ContentLanguage>,
            },
          },
          {
            onSuccess: () => {
              predefinedToasts.success.updated();
            },
          },
        );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Modal.Content
        title={`${t('transmissionActs.title', {
          count: 1,
        })} #${transmissionActSequence}`}
        actions={[
          {
            variant: 'neutral-outline',
            onClick: () => closeModal(),
            label: t('common.cancel'),
          },
          {
            variant: 'secondary',
            onClick: () => onUpdate(),
            label: t('common.save'),
            disabled: isUpdatingCompany || isUpdatingClient,
          },
          {
            variant: 'primary',
            label: t('buttons.send'),
            type: 'submit',
            disabled: isSendingPdf,
          },
        ]}
      >
        <Stack gap={2}>
          <Controller
            name="accountantLanguage"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => {
              return (
                <SelectInput
                  label={t('marketplaceItems.translationLanguage')}
                  name={name}
                  value={value}
                  onChange={onChange}
                  options={uppercaseAppLanguages.map((language) => ({
                    label: formatLanguage(i18n.language, language),
                    value: language,
                  }))}
                  ref={ref}
                />
              );
            }}
          />

          <Controller
            name="contentText"
            control={control}
            rules={{
              required: {
                value: true,
                message: t('form.required'),
              },
            }}
            render={({ field: { value, onChange, ref }, fieldState }) => (
              <RichTextInput
                label={t('transmissionActs.emailContent')}
                value={value}
                onChange={onChange}
                error={fieldState.error?.message}
                minHeight={240}
                maxHeight={500}
                ref={ref}
              />
            )}
          />

          <Stack gap={0.5}>
            <Typography variant="p1" color="textSecondary">
              {t('common.recipients')}
            </Typography>
            <SortableList
              key={JSON.stringify(
                accountantEmailsFields.map((field) => field.id),
              )}
              disabled
              items={accountantEmailsFields.map((field, i) => ({
                id: field.id,
                content: (
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="stretch"
                    py={0.5}
                    spacing={2}
                    key={field.id}
                  >
                    <Box flex={1}>
                      <Controller
                        name={`accountantEmails.${i}.email`}
                        control={control}
                        render={({ field: { name, value, onChange, ref } }) => (
                          <TextInput
                            label={t('common.email')}
                            name={name}
                            value={value}
                            onChange={onChange}
                            type="email"
                            sx={{ width: '100%' }}
                            required
                            ref={ref}
                          />
                        )}
                      />
                    </Box>

                    <Box flexShrink={0}>
                      <Button
                        variant="neutral-outline"
                        size="md"
                        icon={Delete}
                        onClick={() => removeAccountantEmail(i)}
                        disabled={accountantEmailsFields.length <= 1}
                        title={t('common.remove')}
                      />
                    </Box>
                  </Stack>
                ),
              }))}
            />

            <Box>
              <Button
                label={t('common.add')}
                variant="tertiary"
                onClick={() => {
                  appendAccountantEmail({ email: '' });
                }}
              />
            </Box>
          </Stack>
        </Stack>
      </Modal.Content>
    </form>
  );
};
